import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Dialog, TextArea } from '@dsi/react-eds';
import { useLoader } from '../../hooks/useLoader';
import { useApprovePromoteForm } from './approve-promote-form';
import styles from './TestExecutionDialog.module.css';
import ReactDOM from 'react-dom';
import TestExecutionSummary from '../TestAutomation/TestExecutionSummary/TestExecutionSummary';
import { PipelineItem } from '../../const/PipelineTypes';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import api from '../../api/axiosInstance';
import { TestExecution } from './TestExecution/const/TestExecution';
import keycloak from '../../services/keycloak';
import { AxiosError } from 'axios';

export interface ApprovePromoteDialogProps {
  open: boolean;
  onlyReview: boolean;
  onClose: () => void;
  onApproveAndPromote: () => void;
  pipeline: PipelineItem | null;
}

const ApprovePromoteDialog: React.FC<ApprovePromoteDialogProps> = ({ open, onlyReview, onClose, onApproveAndPromote, pipeline }) => {
  const {
    formState: { isValid },
    reset,
    control,
    getValues
  } = useApprovePromoteForm();

  const { showLoader, hideLoader } = useLoader();

  const { addNotifications } = useSwobNotifications();

  const [testExecutionId, setTestExecutionId] = useState<string>();

  useEffect(() => {
    const fetchTestExecutionsByTicketId = async () => {
      const url = `/api/test-executions/${pipeline?.TicketId}`;
      const response = await api.get(url);
      if (response.status === 200) {
        const testExecutions: TestExecution[] = response.data || [];
        const lastExecution = testExecutions.reduce((latest, execution) => {
          return new Date(latest.updatedAt) > new Date(execution.updatedAt) ? latest : execution;
        });
        if (lastExecution) {
          setTestExecutionId(lastExecution.testExecutionId);
        }
      } else {
        console.error(response.statusText);
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description: `An unexpected error occurred while fecthing executions for Ticket ID: ${pipeline?.TicketId}`,
            timestamp: new Date(),
            color: 'red'
          }
        ]);
      }
    };
    if (pipeline) {
      fetchTestExecutionsByTicketId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      reset();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, reset]);

  const handleFormSubmit = async (data: { reasonForOverriding: string }) => {
    if (data) {
      //save reason for overriding
      showLoader();
      try {
        const baseApi = `/api/workflows-actions/approve-promote`;
        const submitData = {
          workflowId: pipeline?.TicketId,
          automationFowId: pipeline?.automationFlow?.id,
          testExecutionId: testExecutionId || null,
          comment: data.reasonForOverriding,
          username: keycloak.tokenParsed?.preferred_username || ''
        };
        const res = await api.put(baseApi, submitData);
        console.log(res);
        addNotifications([
          {
            title: 'Success',
            icon: 'icon-success',
            description: `Artifacts approved & promoted for ${pipeline?.TicketId} Successfully.`,
            timestamp: new Date(),
            color: 'green'
          }
        ]);
        hideLoader();
      } catch (error) {
        console.error(error);
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description:
              error && error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : 'An error occurred while promoting artifacts',
            timestamp: new Date(),
            color: 'red'
          }
        ]);
        hideLoader();
      }
      onApproveAndPromote();
      handleCancel();
    }
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return ReactDOM.createPortal(
    <ReactEDSWrapper
      component={Dialog}
      data-type="fullscreen"
      className={'fullscreen approve-and-promote-dialog'}
      show={open}
      onClose={handleCancel}
      title="Approve and Promote Workflow"
      bottom={
        !onlyReview && (
          <ReactEDSWrapper
            component={Button}
            variant="primary"
            className="btn primary"
            type="submit"
            disabled={!isValid}
            onClick={() => handleFormSubmit(getValues())}
          >
            Approve & Promote
          </ReactEDSWrapper>
        )
      }
    >
      <form id="approve-promote-form" className={styles.testExecutionForm}>
        {/* Test Execution Summary Section */}
        <div style={{ marginBottom: '16px' }}>
          <p>
            The Cloud RAN LCM Automation testing stage result is warning, some test cases failed. Proceeding to production without passing
            all tests may introduce risks os instability
          </p>
          {!onlyReview && (
            <p>
              Are you sure you want to force this workflow to production? Please review the failed test cases and provide a reason for
              overriding the test failure. This will be recorded for auditing purposes
            </p>
          )}
        </div>

        {/* Reason for Overriding Section */}
        {!onlyReview && (
          <div className={styles.reasonSection}>
            <label htmlFor="reason-for-overriding" className={`${styles.whiteText} ${styles.reasonLabel}`}>
              Reason for Overriding
            </label>
            <Controller
              name="reasonForOverriding"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <ReactEDSWrapper
                    component={TextArea}
                    id="reason-for-overriding"
                    placeholder="Enter reason for overriding the normal workflow process..."
                    rows={4}
                    {...field}
                    className={error ? `${styles.textAreaField} ${styles.inputError}` : styles.textAreaField}
                  />
                  {error && (
                    <div className={styles.errorContainer}>
                      <span className={styles.error}>
                        <span className={styles.dangerIcon} aria-label="error" role="img">
                          &#9888;
                        </span>
                        {error.message}
                      </span>
                    </div>
                  )}
                </>
              )}
            />
          </div>
        )}
      </form>
      <div>{testExecutionId && <TestExecutionSummary testExecutionId={testExecutionId} isDialog={true} />}</div>
    </ReactEDSWrapper>,
    document.body
  );
};

export default ApprovePromoteDialog;
